/**
 * Types for CLI remote task execution.
 *
 * These types define the domain model for remote task dispatch, status
 * tracking, batch operations, and wait-for-completion workflows.
 */

export interface Config {
  region?: string;
  size?: string;
  idleTimeout?: number;  // Minutes of inactivity before auto-shutdown (0 = disabled)
  apiUrl?: string;       // Backend API URL
}

export const DEFAULT_REGION = 'iad';
export const DEFAULT_SIZE = 'performance-8x';  // 8 dedicated CPUs, 16GB RAM

// API proxy for round-robin token rotation (avoids rate limits)
export const API_PROXY_URL = 'https://api.spacecode.app';

// Default backend API URL (Cloudflare Workers deployed backend)
export const DEFAULT_API_URL = 'https://backend.spacecode.app';

// Sandbox Worker URL for headless task invocation
export const SANDBOX_WORKER_URL = typeof process !== 'undefined' && process.env?.SANDBOX_WORKER_URL
  ? process.env.SANDBOX_WORKER_URL
  : 'https://sandbox.spacecode.app';

/** Proxy routing mode */
export type ProxyMode = 'legacy' | 'pipeline';

/** Build the proxy URL based on mode. Legacy mode adds /legacy prefix. */
export function getProxyUrl(mode: ProxyMode = 'pipeline'): string {
  return mode === 'legacy' ? `${API_PROXY_URL}/legacy` : API_PROXY_URL;
}

/** Mode for remote VMs */
export type ClaudeMode = 'interactive' | 'task' | 'worker';

/** Status of a remote task */
export type TaskStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

/** A remote task dispatched to a VM */
export interface RemoteTask {
  id: string;
  machineId: string;
  machineName: string;
  repo: string;
  prompt: string;
  mode: 'exec' | 'stream' | 'headless';
  status: TaskStatus;
  region: string;
  size: string;
  createdAt: string;
  completedAt?: string;
  exitCode?: number;
  prUrl?: string;
  branch?: string;
  error?: string;
  groupId?: string;
  committed?: boolean;
  pushed?: boolean;
}

/** Usage tracking history */
export interface UsageHistory {
  totalSessions: number;
  lastSessionAt?: string;
}

/** Options for the task dispatch commands */
export interface TaskOptions {
  repo: string;
  prompt: string;
  idleTimeout?: number;
  branch?: string;
  noCommit?: boolean;
  proxyMode?: ProxyMode;
  region?: string;
  size?: string;
  memoryMb?: number;
}

/** Specification for a single task in a batch dispatch */
export interface BatchTaskSpec {
  repo: string;
  prompt: string;
  branch?: string;
  region?: string;
  size?: string;
}

/** Result of dispatching a single task within a batch */
export interface BatchTaskResult {
  taskId: string;
  repo: string;
  branch: string;
  status: 'dispatched' | 'failed';
  machineId?: string;
  error?: string;
}

/** Result of a batch dispatch operation */
export interface BatchDispatchResult {
  groupId: string;
  total: number;
  dispatched: number;
  failed: number;
  tasks: BatchTaskResult[];
}

/** Result of waiting for tasks to complete */
export interface WaitResult {
  completed: number;
  failed: number;
  cancelled: number;
  timedOut: boolean;
  tasks: Array<{
    taskId: string;
    repo: string;
    branch?: string;
    status: TaskStatus;
    prUrl?: string;
    error?: string;
    durationSeconds?: number;
  }>;
}

/** Structured result from headless mode (--output-format json). */
export interface HeadlessResult {
  taskId: string;
  repo: string;
  branch?: string;
  result: string;
  exitCode: number;
  success: boolean;
  diff: string;
  durationSeconds: number;
  error?: string;
}
